import { FormikField } from './FormikFields';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes from 'prop-types';
import React from 'react';
import { TextField } from './TextField';
import moment from 'moment';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';

const PickerTextField = React.forwardRef(
    ({ ownerState, focused, FormControlProps, ...props }, ref) => (
        <TextField {...props} FormControlProps={{ ...FormControlProps, focused }} ref={ref} />
    )
);
PickerTextField.displayName = 'PickerTextField';
PickerTextField.propTypes = {
    FormControlProps: PropTypes.object,
    focused: PropTypes.bool,
    ownerState: PropTypes.object,
};

/**
 * @typedef {Object} Base
 * @property {Object=} translationOptions The `options` object provided to all internal <Locale/> translations for the TextField
 * @typedef {Base & Pick<import('@mui/material').FormControlProps, 'error' | 'fullWidth' | 'required'> & Pick<import('@mui/material').TextFieldProps, 'helperText' | 'placeholder'> & import('@mui/x-date-pickers').DatePickerProps} DatePickerProps
 */

/**
 * @type {React.ForwardRefExoticComponent<DatePickerProps>}
 */
const DatePicker = React.forwardRef((props, ref) => {
    const {
        defaultValue,
        disabled,
        error,
        fullWidth,
        inputFormat = 'MM/DD/YYYY',
        helperText,
        inputProps,
        maxDate,
        minDate,
        onAccept,
        onChange,
        onError,
        placeholder,
        required,
        slotProps,
        value: providedValue,
        ...remain
    } = props;

    // DatePicker can never be uncontrolled, so we need to keep track of the date internally so it updates correctly
    const [value, setValue] = React.useState(providedValue ?? null);

    const handleAccept = useBoundCallback(
        (onAccept, selectedDate) => {
            // Currently using moment.js, so we must sent the ISO timestamp to the handler using `valueOf()`
            const timestamp = selectedDate ? selectedDate.valueOf() : null;
            setValue(timestamp);
            onAccept && onAccept(timestamp);
        },
        [onAccept]
    );

    const handleChange = useBoundCallback(
        (onChange, onError, dateOrNull, textFieldValue) => {
            // Currently using moment.js, so we must sent the ISO timestamp to the handler using `valueOf()`
            const timestamp = dateOrNull && dateOrNull.valueOf();
            // timestamp is NaN when the input date is invalid according to the format
            if (!isNaN(timestamp) || textFieldValue === undefined) {
                // Valid timestamp should clear the existing error
                onError && onError(null, textFieldValue);
                setValue(timestamp);
            } else {
                // Invalid date
                onError && onError('invalidDate', textFieldValue);
            }
            onChange && onChange(timestamp, textFieldValue);
        },
        [onChange, onError]
    );

    const pickerValue = React.useMemo(() => (value != null ? moment(value) : null), [value]);

    const textFieldProps = React.useMemo(
        () => ({
            ...(placeholder && { placeholder }),
            fullWidth,
            error: !!error,
            helperText,
            required,
            inputProps,
        }),
        [error, fullWidth, helperText, inputProps, placeholder, required]
    );

    return (
        <MuiDatePicker
            ref={ref}
            disabled={disabled}
            format={inputFormat}
            onAccept={handleAccept}
            onChange={handleChange}
            onError={onError}
            slots={{ textField: PickerTextField }}
            slotProps={{ ...slotProps, textField: { ...slotProps?.textField, ...textFieldProps } }}
            value={pickerValue}
            {...remain}
            minDate={minDate != null ? moment(minDate) : undefined}
            maxDate={maxDate != null ? moment(maxDate) : undefined}
        />
    );
});

DatePicker.propTypes = {
    autoOk: PropTypes.bool,
    DatePickerProps: PropTypes.object,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    inputFormat: PropTypes.string,
    inputProps: PropTypes.object,
    label: PropTypes.string,
    localeCode: PropTypes.string,
    helperText: PropTypes.any,
    InputLabelProps: PropTypes.object,
    maxDate: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
    minDate: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
    onAccept: PropTypes.func,
    onChange: PropTypes.func,
    onError: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    slotProps: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DatePicker;

// Built inline rather than via withFormikField: calling a hoisted HOC from
// another module at module scope reads that module's `React` binding before
// its body has run whenever the bundler enters this import cycle from the
// other side. Dereferencing FormikField at render time avoids that entirely.
export const FormikDatePicker = React.forwardRef((props, ref) => (
    <FormikField {...props} type="date" component={DatePicker} ref={ref} />
));
FormikDatePicker.displayName = 'FormikDatePicker';
